import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react'
import { cn } from '@/lib/utils'

const REVIEW_KEYS = [
  'reviews.review1',
  'reviews.review2',
  'reviews.review3',
  'reviews.review4',
  'reviews.review5',
  'reviews.review6',
]

const AUTO_INTERVAL = 6000

const slideVariants = {
  enter: (dir: number) => ({
    x: dir > 0 ? 80 : -80,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (dir: number) => ({
    x: dir > 0 ? -80 : 80,
    opacity: 0,
  }),
}

export function ReviewsSection() {
  const { t } = useTranslation()
  const [[current, direction], setCurrent] = useState([0, 0])

  const total = REVIEW_KEYS.length

  const go = useCallback(
    (dir: number) => {
      setCurrent(([prev]) => [(prev + dir + total) % total, dir])
    },
    [total],
  )

  // Auto-advance
  useEffect(() => {
    const id = setInterval(() => go(1), AUTO_INTERVAL)
    return () => clearInterval(id)
  }, [go])

  const key = REVIEW_KEYS[current]

  return (
    <section className="section-padding overflow-hidden">
      <div className="container-main">
        {/* Header */}
        <div className="text-center mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-4xl font-bold mb-4"
          >
            {t('reviews.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-text-muted max-w-2xl mx-auto text-lg"
          >
            {t('reviews.subtitle')}
          </motion.p>
        </div>

        {/* Review card */}
        <div className="relative max-w-3xl mx-auto">
          {/* Decorative quote */}
          <Quote className="absolute -top-4 left-6 md:left-10 w-12 h-12 text-accent/10 rotate-180" />

          <div className="relative min-h-[260px] md:min-h-[220px] flex items-center">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={current}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
                className="w-full px-4 md:px-16"
              >
                {/* Stars */}
                <div className="flex gap-1 justify-center mb-6">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 fill-amber-400 text-amber-400"
                    />
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="text-lg md:text-xl text-text-heading leading-relaxed text-center font-medium italic mb-8">
                  &ldquo;{t(`${key}.quote`)}&rdquo;
                </blockquote>

                {/* Author */}
                <div className="text-center">
                  <div className="font-semibold text-text-heading">
                    {t(`${key}.author`)}
                  </div>
                  <div className="text-sm text-text-muted mt-0.5">
                    {t(`${key}.role`)}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Arrows */}
          <button
            onClick={() => go(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-6 w-10 h-10 rounded-full bg-surface-light border border-surface-border flex items-center justify-center text-text-muted hover:text-accent hover:border-accent transition-all"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => go(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-6 w-10 h-10 rounded-full bg-surface-light border border-surface-border flex items-center justify-center text-text-muted hover:text-accent hover:border-accent transition-all"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2.5 mt-10">
          {REVIEW_KEYS.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent([i, i > current ? 1 : -1])}
              aria-label={`Review ${i + 1}`}
              className="group p-1"
            >
              <span
                className={cn(
                  'block rounded-full transition-all duration-300',
                  i === current
                    ? 'w-8 h-2.5 bg-accent'
                    : 'w-2.5 h-2.5 bg-text-muted/30 group-hover:bg-accent/50',
                )}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  )
}
